import { useEffect, useRef, useState } from "react";
import { CostoNonNotoBox } from "./CostoNonNotoBox";
import type { EditablePackage } from "../packagesEditing";
import {
  invalidPackagesCount,
  recalcForCount,
  samePackages,
  totalWeight,
} from "../packagesEditing";

/** Ordine in anteprima per il popup colli (forma comune Cosma + WF). */
export type PackagesPreviewOrder = {
  order_id: number;
  reference: string;
  shipping: {
    customer_name: string;
    address: string;
    postcode: string;
    city: string;
    province: string;
    country: string;
    phone: string;
  };
  items: Array<{ sku: string; title: string; quantity: number }>;
  packages: EditablePackage[];
  unit_packages: EditablePackage[];
  packages_source: "saved" | "catalog";
  already_sent: boolean;
  warnings: string[];
};

export type PackagesPreviewError = { order_id: string; message: string };

interface PackagesConfirmDialogProps {
  previews: PackagesPreviewOrder[];
  previewErrors: PackagesPreviewError[];
  sending: boolean;
  sendError: string | null;
  /** Conferma: riceve SOLO gli override degli ordini modificati. */
  onConfirm: (overrides: Record<string, EditablePackage[]>) => void;
  onClose: () => void;
  /** Branding/parametri per-fornitore (Cosma | Web Furniture). */
  testIdPrefix: string;
  titleId: string;
  title: string;
  subtitle: (count: number) => string;
  confirmLabel: (count: number) => string;
}

const FIELD_LABELS: Array<{ key: keyof EditablePackage; label: string }> = [
  { key: "kg", label: "kg" },
  { key: "l", label: "L cm" },
  { key: "a", label: "A cm" },
  { key: "p", label: "P cm" },
];

/**
 * Popup di conferma invio con editor colli per-collo (kg/L/A/P) + stepper
 * numero colli. Pattern modale condiviso dei dialog di questa cartella
 * (role=dialog, overlay, Esc + click-fuori — bloccati durante l'invio). La
 * conferma invia
 * SOLO gli ordini con colli modificati (dirty tracking); per i default da
 * catalogo l'override parte SEMPRE (WYSIWYG: ciò che vedi è ciò che parte).
 * Generico Cosma/WF via `testIdPrefix` e testi parametrici.
 */
export function PackagesConfirmDialog({
  previews,
  previewErrors,
  sending,
  sendError,
  onConfirm,
  onClose,
  testIdPrefix,
  titleId,
  title,
  subtitle,
  confirmLabel,
}: PackagesConfirmDialogProps) {
  const [pkgsByRef, setPkgsByRef] = useState<Record<string, EditablePackage[]>>(() =>
    Object.fromEntries(
      previews.map((order) => [order.reference, order.packages.map((pkg) => ({ ...pkg }))]),
    ),
  );
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  // Focus iniziale in un effect SUO, deps ``[]``: scatta al montaggio e basta.
  // Stava insieme al listener Esc, le cui deps includono ``onClose``/``sending``
  // — e ``onClose`` e' una arrow inline del parent, quindi identita' nuova a
  // ogni render. OrdiniPage si ri-renderizza almeno ogni 60 s (il polling di
  // ``useRitiriOggi`` scrive un oggetto nuovo), quindi l'effect si rieseguiva e
  // riportava il focus sul bottone Annulla mentre l'operatore stava scrivendo
  // le misure dei colli. Stesso pattern gia' in MailAccountsCard.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  function setCount(order: PackagesPreviewOrder, count: number) {
    setPkgsByRef((prev) => ({
      ...prev,
      [order.reference]: recalcForCount(order.unit_packages, count),
    }));
  }

  function setField(ref: string, index: number, field: keyof EditablePackage, value: number) {
    setPkgsByRef((prev) => ({
      ...prev,
      [ref]: (prev[ref] ?? []).map((pkg, i) =>
        i === index ? { ...pkg, [field]: Number.isFinite(value) && value >= 0 ? value : 0 } : pkg,
      ),
    }));
  }

  const invalidTotal = previews.reduce(
    (sum, order) => sum + invalidPackagesCount(pkgsByRef[order.reference] ?? []),
    0,
  );
  const confirmDisabled = sending || previews.length === 0 || invalidTotal > 0;

  function handleConfirm() {
    if (confirmDisabled) return;
    const overrides: Record<string, EditablePackage[]> = {};
    for (const order of previews) {
      const current = pkgsByRef[order.reference] ?? [];
      // WYSIWYG: per i default da catalogo l'override parte SEMPRE (senza, il
      // server potrebbe applicare la sua logica di default e la LDV non
      // corrisponderebbe al popup). Per i colli salvati basta il dirty tracking.
      if (order.packages_source === "catalog" || !samePackages(current, order.packages)) {
        overrides[order.reference] = current;
      }
    }
    onConfirm(overrides);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !sending) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={titleId} className="font-serif text-[17px] font-medium text-ink">
            {title}
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-3">{subtitle(previews.length)}</p>
        </header>

        <div className="max-h-[65vh] overflow-y-auto px-5 py-4">
          {previewErrors.length > 0 && (
            <div role="alert" className="mb-3 rounded-sm border border-line bg-paper px-3 py-2">
              {previewErrors.map((err) => (
                <p key={err.order_id} className="text-[11.5px] text-red">
                  Ordine {err.order_id}: {err.message} — escluso dall'invio
                </p>
              ))}
            </div>
          )}

          {previews.map((order) => {
            const pkgs = pkgsByRef[order.reference] ?? [];
            return (
              <details
                key={order.reference}
                open={previews.length === 1}
                className="mb-4 rounded-sm border border-line last:mb-0"
              >
                <summary className="flex cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2">
                  <span className="font-mono text-[12px] font-medium text-ink">
                    {order.reference}
                  </span>
                  <span className="text-[12px] text-ink-2">{order.shipping.customer_name}</span>
                  {order.already_sent && (
                    <span className="rounded-sm bg-paper px-1.5 py-0.5 text-[10.5px] font-medium uppercase tracking-wide text-ink-2">
                      già inviato
                    </span>
                  )}
                  <span className="ml-auto text-[11.5px] tabular-nums text-ink-3">
                    {pkgs.length} coll{pkgs.length === 1 ? "o" : "i"} — tot {totalWeight(pkgs)} kg
                  </span>
                </summary>

                <div className="border-t border-line px-3 py-2.5">
                  <CostoNonNotoBox riferimento={order.reference} />
                  <p className="text-[11.5px] text-ink-2">
                    {order.shipping.address}, {order.shipping.postcode} {order.shipping.city}
                    {order.shipping.province ? ` (${order.shipping.province})` : ""} —{" "}
                    {order.shipping.country}
                    {order.shipping.phone ? ` · tel ${order.shipping.phone}` : ""}
                  </p>

                  <ul className="mt-1.5 text-[11.5px] text-ink-2">
                    {order.items.map((item) => (
                      <li key={item.sku}>
                        <span className="font-mono">{item.sku}</span>
                        {item.title ? ` — ${item.title}` : ""} × {item.quantity}
                      </li>
                    ))}
                  </ul>

                  {order.warnings.map((warning) => (
                    <p key={warning} className="mt-1.5 text-[11px] text-red">
                      {warning}
                    </p>
                  ))}

                  <div className="mt-2.5 flex items-center gap-2">
                    <label
                      htmlFor={`${testIdPrefix}-colli-count-${order.reference}`}
                      className="text-[11.5px] font-medium text-ink-2"
                    >
                      N° colli
                    </label>
                    <input
                      id={`${testIdPrefix}-colli-count-${order.reference}`}
                      data-testid={`${testIdPrefix}-colli-count-${order.reference}`}
                      type="number"
                      min={1}
                      max={50}
                      value={pkgs.length}
                      disabled={sending}
                      onChange={(event) => setCount(order, Number(event.target.value))}
                      className="w-16 rounded-sm border border-line px-2 py-1 text-[12px] tabular-nums focus:outline-none focus:border-line-2 focus:ring-1 focus:ring-accent/40 disabled:opacity-50"
                    />
                  </div>

                  <div className="mt-2 space-y-1.5">
                    {pkgs.map((pkg, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="w-14 text-[11px] text-ink-3">Collo {index + 1}</span>
                        {FIELD_LABELS.map(({ key, label }) => (
                          <label
                            key={key}
                            className="flex items-center gap-1 text-[11px] text-ink-3"
                          >
                            <input
                              data-testid={`${testIdPrefix}-pkg-${order.reference}-${index}-${key}`}
                              type="number"
                              min={0}
                              step={key === "kg" ? 0.1 : 1}
                              inputMode="decimal"
                              value={pkg[key]}
                              disabled={sending}
                              aria-label={`Collo ${index + 1} ${label}`}
                              onChange={(event) =>
                                setField(order.reference, index, key, Number(event.target.value))
                              }
                              className="w-[72px] rounded-sm border border-line px-2 py-1 text-[12px] tabular-nums focus:outline-none focus:border-line-2 focus:ring-1 focus:ring-accent/40 disabled:opacity-50"
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </details>
            );
          })}
        </div>

        <footer className="border-t border-line px-5 py-3">
          {sendError && (
            <p role="alert" className="mb-2 text-[11.5px] text-red">
              {sendError}
            </p>
          )}
          {invalidTotal > 0 && (
            <p
              data-testid={`${testIdPrefix}-invalid-packages-msg`}
              className="mb-2 text-[11.5px] text-red"
            >
              {invalidTotal} coll{invalidTotal === 1 ? "o" : "i"} senza peso: inserisci un peso
              maggiore di zero per confermare.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              data-testid={`${testIdPrefix}-cancel-btn`}
              onClick={onClose}
              disabled={sending}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="button"
              data-testid={`${testIdPrefix}-confirm-btn`}
              onClick={handleConfirm}
              disabled={confirmDisabled}
              aria-busy={sending}
              className="rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Invio in corso…" : confirmLabel(previews.length)}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
